// src/BrilliantShareMarket.js
// Production Institutional Portal for Brilliant Share Market Academy Pune
// Re-architected with modular components, verified closing baselines, and bespoke SVG iconography

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import './BrilliantShareMarket.css';

// Centralized Domain Data
import {
  OFFICIAL_BENCHMARKS,
  OFFICIAL_GAINERS,
  OFFICIAL_TICKERS,
  PILLARS_DATA,
  COURSES_DATA,
  SCHEDULE_DATA,
  REVIEWS_DATA,
  FAQS_DATA,
  getMarketSession,
} from './data/bsmData';

// Centralized SVG Vector Icons
import {
  PhoneIcon,
  WhatsAppIcon,
  GraduationCapIcon,
  LaptopIcon,
  ShieldCheckIcon,
  HandshakeIcon,
  BarChartIcon,
  CandlestickIcon,
  CalculatorIcon,
  LightbulbIcon,
  LockIcon,
  BoltIcon,
  ClockIcon,
  CheckIcon,
  DocIcon,
  MailIcon,
  PinIcon,
  ExternalLinkIcon,
  StarRating,
  ActivityIcon,
  SearchIcon,
  CloseIcon,
} from './components/bsm/BsmIcons';

// Sub-components
import TradingViewChart from './components/bsm/TradingViewChart';
import SparklineSVG from './components/bsm/SparklineSVG';
import PositionCalculator from './components/bsm/PositionCalculator';
import EnrollmentModal from './components/bsm/EnrollmentModal';
import SyllabusModal from './components/bsm/SyllabusModal';
import CourseMatcherQuiz from './components/bsm/CourseMatcherQuiz';
import CourseComparisonMatrix from './components/bsm/CourseComparisonMatrix';
import TraderRoadmap from './components/bsm/TraderRoadmap';
import StickyConversionDock from './components/bsm/StickyConversionDock';
import TransitGuideCard from './components/bsm/TransitGuideCard';

// Helper map to render pillar icons dynamically
const PILLAR_ICON_MAP = {
  GraduationCapIcon: GraduationCapIcon,
  LaptopIcon: LaptopIcon,
  ShieldCheckIcon: ShieldCheckIcon,
  HandshakeIcon: HandshakeIcon,
};

export default function BrilliantShareMarket() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [syllabusModalCourse, setSyllabusModalCourse] = useState(null);
  
  // Real-time market streaming clock (IST)
  const [liveClock, setLiveClock] = useState('15:29:45 IST');
  const [sessionInfo, setSessionInfo] = useState(() => getMarketSession());
  const [dashboardTab, setDashboardTab] = useState('tradingview'); // 'tradingview' | 'board' | 'calc'
  const [tvSymbol, setTvSymbol] = useState('BSE:SENSEX');
  const [tvInterval, setTvInterval] = useState('D');
  const [streamMode, setStreamMode] = useState('freeze'); // 'freeze' | 'live'
  const [openFaq, setOpenFaq] = useState(null);
  const [faqCategory, setFaqCategory] = useState('all');
  const [faqSearch, setFaqSearch] = useState('');

  const filteredFaqs = useMemo(() => {
    return FAQS_DATA.filter((faq) => {
      const matchesCategory = faqCategory === 'all' || faq.category === faqCategory;
      const matchesSearch = !faqSearch.trim() ||
        faq.q.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.a.toLowerCase().includes(faqSearch.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [faqCategory, faqSearch]);

  // Enrollment Form State
  const [enrollForm, setEnrollForm] = useState({
    name: '',
    phone: '',
    email: '',
    course: "Beginner's Stock Market Course",
    preferredMode: 'Classroom at Pune Center (Akurdi)',
    experience: 'Complete Beginner',
    message: '',
  });
  const [formSentMethod, setFormSentMethod] = useState(null);

  // Interactive Risk Calculator State
  const [calcData, setCalcData] = useState({
    capital: 100000,
    riskPercent: 2,
    entryPrice: 1187,
    stopLoss: 1160,
  });

  // Derived mathematical risk calculations with division-by-zero protection
  const {
    riskAmount,
    positionShares,
    totalPositionValue,
    targetPrice1To25,
    targetProfit,
  } = useMemo(() => {
    const riskAmt = calcData.capital * (calcData.riskPercent / 100);
    const pRisk = Math.max(1, Math.abs(calcData.entryPrice - calcData.stopLoss));
    const shares = Math.floor(riskAmt / pRisk);
    const posVal = shares * calcData.entryPrice;
    const tgtPrice = +(calcData.entryPrice + pRisk * 2.5).toFixed(2);
    const tgtProfit = +(shares * (pRisk * 2.5)).toFixed(0);
    return {
      riskAmount: riskAmt,
      positionShares: shares,
      totalPositionValue: posVal,
      targetPrice1To25: tgtPrice,
      targetProfit: tgtProfit,
    };
  }, [calcData]);

  // Live Market State
  const [benchmarks, setBenchmarks] = useState(OFFICIAL_BENCHMARKS);
  const [gainers, setGainers] = useState(OFFICIAL_GAINERS);
  const [tickers, setTickers] = useState(OFFICIAL_TICKERS);

  // Stream mode controller
  const handleSetStreamMode = useCallback((mode) => {
    setStreamMode(mode);
    if (mode === 'freeze') {
      setBenchmarks(OFFICIAL_BENCHMARKS);
      setGainers(OFFICIAL_GAINERS);
      setTickers(OFFICIAL_TICKERS);
    }
  }, []);

  // Live streaming clock updater & session refresher
  useEffect(() => {
    const clockTimer = setInterval(() => {
      const now = new Date();
      setLiveClock(now.toLocaleTimeString('en-IN', { hour12: false }) + ' IST');
      setSessionInfo(getMarketSession());
    }, 1000);
    return () => clearInterval(clockTimer);
  }, []);

  // Practice Stream Tick Engine with unmount timeout protection
  const flashTimeoutRef = useRef(null);

  useEffect(() => {
    if (streamMode !== 'live') return;

    const tickTimer = setInterval(() => {
      setGainers((prev) => {
        const targetIdx = Math.floor(Math.random() * prev.length);
        return prev.map((item, idx) => {
          if (idx !== targetIdx && Math.random() > 0.45) return item;
          const tickDelta = (Math.random() - 0.45) * (item.price * 0.0006);
          const newPrice = +(item.price + tickDelta).toFixed(2);
          const newChange = +(item.change + tickDelta).toFixed(2);
          const newPct = +((newChange / (newPrice - newChange)) * 100).toFixed(2);
          const isUp = newChange >= 0;
          const newSparkline = [...item.sparkline.slice(-9), newPrice];
          return {
            ...item,
            price: newPrice,
            change: newChange,
            pct: newPct,
            up: isUp,
            sparkline: newSparkline,
            tickFlash: tickDelta >= 0 ? 'flash-up' : 'flash-down',
          };
        });
      });

      setBenchmarks((prev) => {
        const targetIdx = Math.floor(Math.random() * prev.length);
        return prev.map((item, idx) => {
          if (idx !== targetIdx && Math.random() > 0.4) return item;
          const tickDelta = (Math.random() - 0.45) * (item.price * 0.0004);
          const newPrice = +(item.price + tickDelta).toFixed(2);
          const newChange = +(item.change + tickDelta).toFixed(2);
          const newPct = +((newChange / (newPrice - newChange)) * 100).toFixed(2);
          const isUp = newChange >= 0;
          const newHigh = Math.max(item.high, newPrice);
          const newLow = Math.min(item.low, newPrice);
          const newSparkline = [...item.sparkline.slice(-9), newPrice];
          return {
            ...item,
            price: newPrice,
            change: newChange,
            pct: newPct,
            up: isUp,
            high: newHigh,
            low: newLow,
            sparkline: newSparkline,
            tickFlash: tickDelta >= 0 ? 'flash-up' : 'flash-down',
          };
        });
      });

      setTickers((prev) =>
        prev.map((t) => {
          if (Math.random() > 0.4) return t;
          const jitter = (Math.random() - 0.46) * (t.price * 0.0004);
          const newPrice = +(t.price + jitter).toFixed(2);
          const newChange = +(t.change + jitter).toFixed(2);
          return {
            ...t,
            price: newPrice,
            change: newChange,
            pct: +((newChange / (newPrice - newChange)) * 100).toFixed(2),
            up: newChange >= 0,
          };
        })
      );

      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
      flashTimeoutRef.current = setTimeout(() => {
        setGainers((prev) => prev.map((g) => (g.tickFlash ? { ...g, tickFlash: null } : g)));
        setBenchmarks((prev) => prev.map((b) => (b.tickFlash ? { ...b, tickFlash: null } : b)));
      }, 700);
    }, 1800);

    return () => {
      clearInterval(tickTimer);
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, [streamMode]);

  const trackEvent = (eventName, params = {}) => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
    } catch {
      // Graceful fallback
    }
  };

  const handleOpenEnroll = useCallback((courseTitle = "Beginner's Stock Market Course") => {
    trackEvent('bsm_enroll_modal_open', { course: courseTitle });
    setEnrollForm((prev) => ({ ...prev, course: courseTitle }));
    setIsModalOpen(true);
    setFormSentMethod(null);
  }, []);

  const handleWhatsAppEnroll = useCallback(() => {
    trackEvent('bsm_whatsapp_lead', {
      course: enrollForm.course,
      mode: enrollForm.preferredMode,
      experience: enrollForm.experience,
    });

    const msg = `*Admission & Demo Enquiry - Brilliant Share Market Academy*\n\n` +
      `*Name:* ${enrollForm.name || 'Not provided'}\n` +
      `*Phone:* ${enrollForm.phone || 'Not provided'}\n` +
      `*Email:* ${enrollForm.email || 'Not provided'}\n` +
      `*Program:* ${enrollForm.course}\n` +
      `*Preferred Mode:* ${enrollForm.preferredMode}\n` +
      `*Experience Level:* ${enrollForm.experience}\n` +
      `*Message:* ${enrollForm.message || 'I would like to attend a free counseling session and know about upcoming batch dates.'}`;
    
    const whatsappUrl = `https://api.whatsapp.com/send?phone=918177887927&text=${encodeURIComponent(msg)}`;
    window.open(whatsappUrl, '_blank');
    setFormSentMethod('whatsapp');
  }, [enrollForm]);

  const handleEmailEnroll = useCallback((clientType = 'gmail') => {
    trackEvent('bsm_email_lead', {
      course: enrollForm.course,
      client: clientType,
    });

    const subject = encodeURIComponent(`Admission Enquiry: ${enrollForm.course} - ${enrollForm.name}`);
    const body = encodeURIComponent(
      `Full Name: ${enrollForm.name}\n` +
      `Phone Number: ${enrollForm.phone}\n` +
      `Email Address: ${enrollForm.email}\n` +
      `Selected Program: ${enrollForm.course}\n` +
      `Preferred Mode: ${enrollForm.preferredMode}\n` +
      `Experience Level: ${enrollForm.experience}\n\n` +
      `Student Query:\n${enrollForm.message || 'Please send the detailed syllabus, fees, and next batch timings.'}`
    );

    let url = `mailto:brilliantsharemarket@gmail.com?subject=${subject}&body=${body}`;
    if (clientType === 'gmail') {
      url = `https://mail.google.com/mail/?view=cm&fs=1&to=brilliantsharemarket@gmail.com&su=${subject}&body=${body}`;
    }
    window.open(url, '_blank');
    setFormSentMethod('email');
  }, [enrollForm]);

  const bsmStructuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Brilliant Share Market Academy Pune",
    "description": "Pune's premier share market training institute providing practical stock market trading courses, technical analysis, and futures & options mentorship.",
    "url": "https://bramhaware.in/brilliantsharemarket",
    "telephone": "+91-8177887927",
    "email": "brilliantsharemarket@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Office No 204, 2nd Floor, Mayur Trade Center, Old Mumbai Pune Highway, Akurdi",
      "addressLocality": "Pimpri-Chinchwad, Pune",
      "postalCode": "411019",
      "addressRegion": "Maharashtra",
      "addressCountry": "IN"
    }
  }), []);

  return (
    <div className="bsm-page theme-midnight">
      <SEO
        title="Brilliant Share Market Academy Pune | Stock Market Classes & Trading Courses"
        description="Pune's premier share market training institute. Learn price action, equity swing trading, and futures & options from NISM certified mentors with 100% practical live market sessions."
        keywords="share market classes pune, stock market training pimpri chinchwad, technical analysis courses pune, brilliant share market academy, trading classes akurdi"
        canonicalUrl="/brilliantsharemarket"
        structuredData={bsmStructuredData}
      />

      {/* 1. Official Top Authority Alert Ribbon */}
      <div className="bsm-top-ribbon">
        <div className="ribbon-inner">
          <div className="ribbon-badge">
            <span className="ribbon-dot" />
            <span>ADMISSIONS OPEN</span>
          </div>
          <p className="ribbon-text">
            Upcoming Batch: <strong>Weekend Intensive &amp; Weekday Morning Batches</strong> &bull; Limited to 15 seats per cohort &bull; Akurdi, Pimpri-Chinchwad &amp; Live Online
          </p>
          <div className="ribbon-actions">
            <Link to="/" className="ribbon-studio-link" title="Return to Bramhaware Technologies Homepage">
              ← Bramhaware
            </Link>
            <a href="tel:+918177887927" className="ribbon-helpline">
              <PhoneIcon width={13} height={13} />
              <span>Helpline: +91 81778 87927</span>
            </a>
            <button
              type="button"
              className="ribbon-cta-btn"
              onClick={() => handleOpenEnroll("Free Counseling & Demo")}
            >
              Book Free Counseling →
            </button>
          </div>
        </div>
      </div>

      {/* 2. Institutional Live Financial Marquee Bar */}
      <div className="bsm-market-ticker-bar" aria-label="Official Market Ticker">
        <div className="ticker-track">
          {[...tickers, ...tickers].map((item, idx) => (
            <div key={idx} className="ticker-chip">
              <span className="ticker-sym">{item.symbol}</span>
              <span className="ticker-val">{item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              <span className={`ticker-change ${item.up ? 'up' : 'down'}`}>
                {item.up ? '▲ +' : '▼ '}{item.pct}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 3. Production Header & Navigation */}
      <header className="bsm-header">
        <div className="bsm-header-inner">
          <div className="bsm-brand-box">
            <div className="bsm-logo-shield">
              <ActivityIcon width={22} height={22} className="logo-svg-mark" />
            </div>
            <div className="bsm-brand-titles">
              <div className="brand-primary-row">
                <span className="brand-main">BRILLIANT</span>
                <span className="brand-tagline">SHARE MARKET ACADEMY</span>
              </div>
              <span className="brand-trust-meta">ESTD. 2016 &bull; NISM CERTIFIED FACULTY &bull; PUNE CENTER</span>
            </div>
          </div>

          <nav className="bsm-nav" aria-label="Main Navigation">
            <a href="#about" className="bsm-nav-link">Why Choose Us</a>
            <a href="#courses" className="bsm-nav-link">Programs &amp; Fees</a>
            <a href="#schedule" className="bsm-nav-link">Batch Calendar</a>
            <a href="#terminal" className="bsm-nav-link">Trading Desk</a>
            <a href="#reviews" className="bsm-nav-link">Reviews</a>
            <a href="#contact" className="bsm-nav-link">Center Location</a>
          </nav>

          <div className="bsm-header-actions">
            <a href="tel:+918177887927" className="btn-header-call" title="Call Admissions Desk">
              <PhoneIcon width={14} height={14} />
              <span>+91 81778 87927</span>
            </a>
            <button
              type="button"
              className="btn-header-enroll"
              onClick={() => handleOpenEnroll("Beginner's Stock Market Course")}
            >
              <span>Enroll in Batch →</span>
            </button>
          </div>
        </div>
      </header>

      {/* 4. Spacious, High-Credibility Institutional Hero Section (With Real Photography) */}
      <section className="bsm-hero-section">
        <div className="bsm-hero-grid">
          {/* Left Column: Core Value Proposition */}
          <div className="bsm-hero-content">
            <div className="hero-trust-badge">
              <StarRating count={5} />
              <span className="badge-text">
                <strong>4.9 / 5 Rating</strong> &bull; 1,240+ Verified Pune Students &bull; NISM Certified Faculty
              </span>
            </div>

            <h1 className="hero-main-title">
              Master Stock Market Trading with <span className="text-highlight">Disciplined, Institutional Edge.</span>
            </h1>

            <p className="hero-lead-text">
              Bridging the gap between dry textbook theory and real market execution. Learn systematic price action, swing setups, and derivatives directly on live charts at our Pimpri-Chinchwad classroom or live online cohorts.
            </p>

            {/* Checklist of Educational Guarantees */}
            <div className="hero-check-grid">
              <div className="check-item">
                <CheckIcon className="check-ico" />
                <span><strong>100% Live Market Hours Practice</strong> (9:15 AM - 3:30 PM real execution)</span>
              </div>
              <div className="check-item">
                <CheckIcon className="check-ico" />
                <span><strong>NISM Series VIII Certified Mentors</strong> with 10+ years active market experience</span>
              </div>
              <div className="check-item">
                <CheckIcon className="check-ico" />
                <span><strong>Capital Preservation First:</strong> Mandatory 2% position sizing &amp; strict R:R framework</span>
              </div>
              <div className="check-item">
                <CheckIcon className="check-ico" />
                <span><strong>Lifetime Trading Desk Access</strong> &amp; weekly market preparation calls</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="hero-actions-cluster">
              <button
                type="button"
                className="btn-hero-primary"
                onClick={() => handleOpenEnroll("Beginner's Stock Market Course")}
              >
                <span>Book Free Demo Class &amp; Counseling →</span>
              </button>
              <a href="#courses" className="btn-hero-secondary">
                <span>Explore 3 Programs ↓</span>
              </a>
              <a
                href="https://api.whatsapp.com/send?phone=918177887927&text=Hello%20Brilliant%20Share%20Market%20Academy,%20I%20want%20to%20know%20about%20upcoming%20batches."
                target="_blank"
                rel="noreferrer"
                className="btn-hero-whatsapp"
              >
                <WhatsAppIcon width={16} height={16} />
                <span>WhatsApp Desk</span>
              </a>
            </div>

            {/* Metric Strip */}
            <div className="hero-metrics-bar">
              <div className="metric-box">
                <strong className="num">1,200+</strong>
                <span className="lbl">Traders Mentored</span>
              </div>
              <div className="metric-divider" />
              <div className="metric-box">
                <strong className="num">10+ Years</strong>
                <span className="lbl">Market Experience</span>
              </div>
              <div className="metric-divider" />
              <div className="metric-box">
                <strong className="num">100%</strong>
                <span className="lbl">Live Market Practice</span>
              </div>
              <div className="metric-divider" />
              <div className="metric-box">
                <strong className="num">Pune Center</strong>
                <span className="lbl">Akurdi / Chinchwad</span>
              </div>
            </div>
          </div>

          {/* Right Column: Authentic Photography Showcase Card with Closing Snapshot */}
          <div className="hero-photo-showcase-card">
            <div className="photo-media-container">
              <img
                src="/assets/bsm_classroom.jpg"
                alt="Brilliant Share Market Academy Live Trading Floor Classroom Pune"
                className="hero-classroom-photo"
              />
              <div className="photo-overlay-glass">
                <span className="photo-glass-badge live">
                  <span className="live-dot" />
                  Live Floor Practice (9:15 AM - 3:30 PM)
                </span>
                <span className="photo-glass-badge nism">
                  <ShieldCheckIcon width={13} height={13} />
                  NISM Series VIII Faculty
                </span>
              </div>
            </div>

            <div className="photo-trust-chip">
              <div className="chip-location">
                <PinIcon width={14} height={14} className="pin-ico" />
                <span>Akurdi Campus &bull; Pimpri-Chinchwad, Pune</span>
              </div>
              <span className="chip-badge">Admissions: 15 Seats Only</span>
            </div>

            {/* Verified Closing Snapshot Banner */}
            <div className="photo-details-strip">
              <div className="closing-snapshot-row">
                <div className="tb-item">
                  <span className="tb-name">NIFTY 50</span>
                  <strong className="tb-val">22,620.45</strong>
                  <span className="tb-chg down">-0.70%</span>
                </div>
                <div className="tb-divider" />
                <div className="tb-item">
                  <span className="tb-name">SENSEX</span>
                  <strong className="tb-val">72,480.29</strong>
                  <span className="tb-chg down">-0.40%</span>
                </div>
                <div className="tb-divider" />
                <div className="tb-item">
                  <span className="tb-name">BANK NIFTY</span>
                  <strong className="tb-val">54,633.05</strong>
                  <span className="tb-chg up">+0.30%</span>
                </div>
              </div>

              <div className="photo-desk-link-row">
                <a href="#terminal" className="btn-photo-desk-link">
                  <CandlestickIcon width={14} height={14} />
                  <span>Open Institutional Trading Desk &amp; Charts ↓</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Container with Expansive Breathing Room */}
      <main className="bsm-main-content">
        {/* ===================================================================
            SECTION 01: WHY SERIOUS TRADERS CHOOSE BRILLIANT ACADEMY
            =================================================================== */}
        <section id="about" className="content-section">
          <div className="section-head">
            <span className="eyebrow-tag">01 // INSTITUTIONAL METHODOLOGY</span>
            <h2 className="section-heading">Why Serious Learners Choose Brilliant Share Market Academy</h2>
            <p className="section-subtext">
              We do not sell get-rich-quick fantasies. We develop disciplined, systematic market operators through practical live floor experience.
            </p>
          </div>

          <div className="pillars-grid-4">
            {PILLARS_DATA.map((pillar) => {
              const IconComp = PILLAR_ICON_MAP[pillar.iconType] || GraduationCapIcon;
              return (
                <div key={pillar.id} className="pillar-card">
                  <div className={`pillar-icon-box ${pillar.colorClass}`}>
                    <IconComp />
                  </div>
                  <h3 className="pillar-title">{pillar.title}</h3>
                  <p className="pillar-desc">{pillar.desc}</p>
                  <ul className="pillar-points">
                    {pillar.points.map((pt, i) => (
                      <li key={i}>&bull; {pt}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* ===================================================================
            SECTION 02: STRUCTURED ACADEMIC PROGRAMS (WITH REAL PHOTOS)
            =================================================================== */}
        <section id="courses" className="content-section">
          <div className="section-head">
            <span className="eyebrow-tag">02 // ACADEMIC PATHWAYS</span>
            <h2 className="section-heading">Structured Stock Market Programs</h2>
            <p className="section-subtext">
              Clear learning progression from beginner fundamentals to systematic equity swing trading and advanced index derivative scalping.
            </p>
          </div>

          {/* Interactive Course Matcher Quiz */}
          <CourseMatcherQuiz onSelectCourse={handleOpenEnroll} />

          <div className="programs-grid-3">
            {COURSES_DATA.map((course) => (
              <div key={course.id} className={`program-card ${course.featured ? 'featured' : ''}`}>
                {course.featured && <div className="featured-flag">MOST POPULAR COHORT</div>}
                
                <div className="program-image-frame">
                  <img
                    src={course.image}
                    alt={course.imageAlt}
                    className="program-cover-img"
                  />
                  <div className="program-image-gradient" />
                  <span className={`program-image-tag ${course.featured ? 'featured' : ''}`}>
                    {course.imageTag}
                  </span>
                </div>

                <div className="program-card-body">
                  <div className="program-badge-strip">
                    <span className={`level-pill ${course.featured ? 'featured' : ''}`}>{course.levelTag}</span>
                    <span className={`target-pill ${course.featured ? 'featured' : ''}`}>{course.targetTag}</span>
                  </div>
                  <h3 className="program-title">{course.title}</h3>
                  <p className="program-tagline">{course.tagline}</p>
                  <p className="program-summary">{course.summary}</p>

                  <div className="program-specs-strip">
                    <div>
                      <span className="spec-label">Duration:</span>
                      <strong>{course.duration}</strong>
                    </div>
                    <div>
                      <span className="spec-label">Mode:</span>
                      <strong>{course.mode}</strong>
                    </div>
                  </div>

                  <div className="program-syllabus-box">
                    <span className="syl-heading">Core Curriculum Modules:</span>
                    <ul className="syl-list">
                      {course.curriculum.map((m, idx) => (
                        <li key={idx}>
                          <CheckIcon className="ico green" /> {m}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="program-card-actions">
                    <button
                      type="button"
                      className={`btn-program-enroll ${course.featured ? 'primary' : ''}`}
                      onClick={() => handleOpenEnroll(course.title)}
                    >
                      Enroll in {course.title.split(' ')[0]} Cohort →
                    </button>
                    <button
                      type="button"
                      className="btn-program-syllabus"
                      onClick={() => setSyllabusModalCourse(course.title)}
                    >
                      <DocIcon width={13} height={13} />
                      <span>Download Detailed Syllabus (PDF)</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Interactive Side-by-Side Comparison Matrix */}
          <CourseComparisonMatrix onSelectCourse={handleOpenEnroll} />
        </section>

        {/* ===================================================================
            PEDAGOGY: TRADER'S 4-STAGE EVOLUTION ROADMAP
            =================================================================== */}
        <TraderRoadmap onEnrollClick={handleOpenEnroll} />

        {/* ===================================================================
            SECTION 03: UPCOMING BATCH SCHEDULE & ADMISSION SEATS
            =================================================================== */}
        <section id="schedule" className="content-section">
          <div className="section-head">
            <span className="eyebrow-tag">03 // ADMISSION CALENDAR</span>
            <h2 className="section-heading">Upcoming Pune Classroom &amp; Online Batches</h2>
            <p className="section-subtext">
              We strictly cap every batch to 15 students to guarantee individual mentor attention and one-on-one trade journal reviews.
            </p>
          </div>

          <div className="schedule-table-card">
            <div className="schedule-mobile-scroll-hint" aria-hidden="true">
              <span>⟷ Swipe horizontally to inspect full batch timings, mode &amp; seats remaining</span>
            </div>
            <div className="schedule-table-wrap">
              <table className="schedule-table">
                <thead>
                  <tr>
                    <th>BATCH COHORT</th>
                    <th>START DATE</th>
                    <th>TIMINGS</th>
                    <th>TRAINING MODE</th>
                    <th>SEATS REMAINING</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {SCHEDULE_DATA.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.cohort}</strong>
                        <span className="sub">{row.sub}</span>
                      </td>
                      <td><strong>{row.startDate}</strong></td>
                      <td>{row.timings}</td>
                      <td><span className={`tag-badge ${row.modeType}`}>{row.mode}</span></td>
                      <td><span className={`seat-badge ${row.seatsAlert ? 'alert' : ''}`}>{row.seatsText}</span></td>
                      <td>
                        <button
                          type="button"
                          className="btn-table-book"
                          onClick={() => handleOpenEnroll(row.courseTitle)}
                        >
                          Reserve Seat →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="schedule-card-footer">
              <div className="footer-callout">
                <LightbulbIcon width={15} height={15} className="callout-ico" />
                <span>Can&apos;t find a suitable time? We offer flexible 1-on-1 personalized mentorship batches upon request.</span>
              </div>
              <a href="tel:+918177887927" className="btn-call-counselor">
                <PhoneIcon width={14} height={14} />
                <span>Speak with Academic Counselor: +91 81778 87927</span>
              </a>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION 04: FULL TRADING ROOM WORKSTATION & RISK MANAGEMENT DESK
            =================================================================== */}
        <section id="terminal" className="content-section">
          <div className="section-head">
            <span className="eyebrow-tag">04 // TRADING ROOM WORKSTATION</span>
            <h2 className="section-heading">Professional Market Analysis &amp; Position Sizing Desk</h2>
            <p className="section-subtext">
              Every student is trained to use professional charting terminals and mathematical risk position calculators before risking real capital.
            </p>
          </div>

          <div className="terminal-production-container">
            {/* Top Toolbar */}
            <div className="terminal-prod-toolbar">
              <div className="prod-status">
                <span className={`live-status-dot ${sessionInfo.isOpen ? 'pulse' : ''}`} />
                <strong>{sessionInfo.statusLabel}</strong>
                <span className="sub">&bull; {sessionInfo.subtext} &bull; {liveClock}</span>
              </div>

              <div className="prod-switchers">
                <button
                  type="button"
                  className={`btn-mode-pill ${streamMode === 'freeze' ? 'active' : ''}`}
                  onClick={() => handleSetStreamMode('freeze')}
                >
                  <LockIcon width={12} height={12} />
                  <span>Official Exchange Close (Verified)</span>
                </button>
                <button
                  type="button"
                  className={`btn-mode-pill ${streamMode === 'live' ? 'active live' : ''}`}
                  onClick={() => handleSetStreamMode('live')}
                >
                  <BoltIcon width={12} height={12} />
                  <span>Active Practice Stream</span>
                </button>
              </div>
            </div>

            {/* Workstation Tabs */}
            <div className="terminal-tabs-row" role="tablist">
              <button
                type="button"
                className={`terminal-tab-btn ${dashboardTab === 'tradingview' ? 'active' : ''}`}
                onClick={() => setDashboardTab('tradingview')}
              >
                <CandlestickIcon width={15} height={15} />
                <span>Live Candlesticks (BSE)</span>
              </button>
              <button
                type="button"
                className={`terminal-tab-btn ${dashboardTab === 'board' ? 'active' : ''}`}
                onClick={() => setDashboardTab('board')}
              >
                <BarChartIcon width={15} height={15} />
                <span>Live Market Watch</span>
              </button>
              <button
                type="button"
                className={`terminal-tab-btn ${dashboardTab === 'calc' ? 'active' : ''}`}
                onClick={() => setDashboardTab('calc')}
              >
                <CalculatorIcon width={15} height={15} />
                <span>Position Size Tool</span>
              </button>
            </div>

            {/* View 1: Real TradingView Candlestick Engine with Risk Sidebar */}
            {dashboardTab === 'tradingview' && (
              <div className="terminal-desk-grid">
                <div className="desk-chart-col">
                  <div className="chart-asset-bar">
                    <span className="bar-label">ASSET:</span>
                    {[
                      { label: 'SENSEX 30', symbol: 'BSE:SENSEX' },
                      { label: 'RELIANCE', symbol: 'BSE:RELIANCE' },
                      { label: 'TCS', symbol: 'BSE:TCS' },
                      { label: 'HDFC BANK', symbol: 'BSE:HDFCBANK' },
                      { label: 'INFOSYS', symbol: 'BSE:INFY' },
                    ].map((s) => (
                      <button
                        key={s.symbol}
                        type="button"
                        className={`chart-sym-btn ${tvSymbol === s.symbol ? 'active' : ''}`}
                        onClick={() => setTvSymbol(s.symbol)}
                      >
                        {s.label}
                      </button>
                    ))}

                    <span className="bar-label tf-label">TIMEFRAME:</span>
                    {[
                      { label: 'Daily (Swing)', val: 'D' },
                      { label: '1-Hour (Trend)', val: '60' },
                      { label: '15-Min (Intraday)', val: '15' },
                      { label: 'Weekly (Macro)', val: 'W' },
                    ].map((tf) => (
                      <button
                        key={tf.val}
                        type="button"
                        className={`chart-sym-btn tf-btn ${tvInterval === tf.val ? 'active' : ''}`}
                        onClick={() => setTvInterval(tf.val)}
                      >
                        {tf.label}
                      </button>
                    ))}
                  </div>

                  <div className="chart-wrapper-box">
                    <TradingViewChart symbol={tvSymbol} interval={tvInterval} />
                  </div>
                </div>

                <PositionCalculator
                  calcData={calcData}
                  setCalcData={setCalcData}
                  riskAmount={riskAmount}
                  positionShares={positionShares}
                  totalPositionValue={totalPositionValue}
                  targetPrice1To25={targetPrice1To25}
                  targetProfit={targetProfit}
                  isStandalone={false}
                />
              </div>
            )}

            {/* View 2: Live Market Watch Board */}
            {dashboardTab === 'board' && (
              <div className="terminal-board-view">
                <div className="terminal-table-container">
                  <div className="table-hdr-row">
                    <span>INSTRUMENT</span>
                    <span className="text-center">TREND / RANGE</span>
                    <span className="text-right">LTP (₹) &bull; CHG%</span>
                  </div>
                  {benchmarks.concat(gainers).map((item) => (
                    <div key={item.symbol} className={`table-data-row ${item.tickFlash || ''}`}>
                      <div className="asset-meta">
                        <strong className="asset-sym">{item.symbol}</strong>
                        <span className="asset-full">{item.subtitle || item.company}</span>
                      </div>

                      <div className="trend-meta">
                        <SparklineSVG data={item.sparkline} isUp={item.up} width={76} height={24} />
                      </div>

                      <div className="price-meta text-right">
                        <span className="num-ltp">₹{item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        <span className={`num-chg ${item.up ? 'up' : 'down'}`}>
                          {item.up ? '▲ +' : '▼ '}{item.pct}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="terminal-sentiment-strip">
                  <div className="sentiment-card">
                    <span className="sent-title">BREADTH BIAS</span>
                    <span className="sent-val positive">Bullish Consolidation</span>
                    <span className="sent-note">Advance / Decline Ratio: 1.42x</span>
                  </div>
                  <div className="sentiment-card">
                    <span className="sent-title">VOLATILITY (INDIA VIX)</span>
                    <span className="sent-val neutral">12.85 (-1.2%)</span>
                    <span className="sent-note">Low volatility premium favor</span>
                  </div>
                  <div className="sentiment-card">
                    <span className="sent-title">DATA INTEGRITY</span>
                    <span className="sent-val verified">NSE/BSE Official Closes</span>
                    <span className="sent-note">Verified against live exchange feeds</span>
                  </div>
                </div>
              </div>
            )}

            {/* View 3: Dedicated Full-Width Risk & Position Sizing Calculator */}
            {dashboardTab === 'calc' && (
              <div className="terminal-calc-standalone-view">
                <div className="calc-standalone-header">
                  <div className="badge-wrapper">
                    <span className="calc-badge">MATHEMATICAL RISK DESK</span>
                  </div>
                  <h3>Strict 2% Account Capital Protection Calculator</h3>
                  <p>
                    Institutions survive because they calculate maximum loss before considering profits. Enter your account balance, desired risk percentage, entry price, and technical stop-loss.
                  </p>
                </div>

                <div className="calc-interactive-grid">
                  <div className="calc-inputs-column">
                    <div className="calc-field-group">
                      <label htmlFor="standalone-capital">Total Trading Capital (₹)</label>
                      <input
                        id="standalone-capital"
                        type="number"
                        value={calcData.capital}
                        onChange={(e) => setCalcData((prev) => ({ ...prev, capital: Math.max(0, +e.target.value) }))}
                      />
                      <span className="field-hint">e.g. ₹1,00,000 active demat balance</span>
                    </div>

                    <div className="calc-field-group">
                      <label htmlFor="standalone-risk-percent">Risk Per Trade (%)</label>
                      <input
                        id="standalone-risk-percent"
                        type="number"
                        step="0.5"
                        min="0.5"
                        max="10"
                        value={calcData.riskPercent}
                        onChange={(e) => setCalcData((prev) => ({ ...prev, riskPercent: Math.max(0.1, +e.target.value) }))}
                      />
                      <span className="field-hint">Recommended: 1.0% to 2.0% maximum</span>
                    </div>

                    <div className="calc-fields-row">
                      <div className="calc-field-group">
                        <label htmlFor="standalone-entry-price">Entry Level (₹)</label>
                        <input
                          id="standalone-entry-price"
                          type="number"
                          value={calcData.entryPrice}
                          onChange={(e) => setCalcData((prev) => ({ ...prev, entryPrice: Math.max(0.05, +e.target.value) }))}
                        />
                      </div>
                      <div className="calc-field-group">
                        <label htmlFor="standalone-stop-loss">Stop-Loss Level (₹)</label>
                        <input
                          id="standalone-stop-loss"
                          type="number"
                          value={calcData.stopLoss}
                          onChange={(e) => setCalcData((prev) => ({ ...prev, stopLoss: Math.max(0, +e.target.value) }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="calc-summary-boxes">
                    <div className="summary-box alert-red">
                      <span className="box-lbl">MAX CAPITAL AT RISK</span>
                      <strong className="box-val red">₹{riskAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong>
                      <span className="box-note">Total capital risked if stop-loss hits</span>
                    </div>

                    <div className="summary-box alert-emerald">
                      <span className="box-lbl">ALLOWED POSITION QUANTITY</span>
                      <strong className="box-val emerald">{positionShares} Shares</strong>
                      <span className="box-note">Calculated mathematically by risk buffer</span>
                    </div>

                    <div className="summary-box neutral">
                      <span className="box-lbl">TOTAL ORDER CAPITAL NEEDED</span>
                      <strong className="box-val white">₹{totalPositionValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong>
                      <span className="box-note">Cash required at broker order entry</span>
                    </div>

                    <div className="summary-box alert-gold">
                      <span className="box-lbl">1:2.5 TARGET PROFIT</span>
                      <strong className="box-val gold">₹{targetPrice1To25} (+₹{targetProfit})</strong>
                      <span className="box-note">Target reward for disciplined swing exit</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ===================================================================
            SECTION 05: VERIFIED ALUMNI EXPERIENCES & REVIEWS
            =================================================================== */}
        <section id="reviews" className="content-section">
          <div className="section-head">
            <span className="eyebrow-tag">05 // VERIFIED ALUMNI EXPERIENCES</span>
            <h2 className="section-heading">Trusted by Over 1,200+ Pune Traders</h2>
            <p className="section-subtext">
              Real testimonials from working professionals, business owners, and beginners trained at our Pimpri-Chinchwad academy.
            </p>
          </div>

          <div className="reviews-grid-4">
            {REVIEWS_DATA.map((rev, i) => (
              <div key={i} className="review-card">
                <StarRating count={5} />
                <p className="review-comment">
                  &ldquo;{rev.quote}&rdquo;
                </p>
                <div className="reviewer-box">
                  <div className="avatar-circle">{rev.initials}</div>
                  <div className="reviewer-info">
                    <strong>{rev.name}</strong>
                    <span>{rev.role}</span>
                    <span className="batch-tag">{rev.batch}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===================================================================
            SECTION 06: FREQUENTLY ASKED QUESTIONS (ACCORDION)
            =================================================================== */}
        <section className="content-section">
          <div className="section-head">
            <span className="eyebrow-tag">06 // FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="section-heading">Everything You Need to Know Before Enrolling</h2>
            <p className="section-subtext">
              Have questions? Find direct, transparent answers regarding course eligibility, batch modes, and post-course support.
            </p>
          </div>

          {/* FAQ Search & Category Filter Controls */}
          <div className="faq-filter-controls">
            <div className="faq-search-box">
              <SearchIcon width={16} height={16} className="faq-search-ico" />
              <input
                type="text"
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
                placeholder="Search questions (e.g. fees, demat, live market, weekend)..."
                className="faq-search-input"
              />
              {faqSearch && (
                <button
                  type="button"
                  className="btn-faq-clear"
                  onClick={() => setFaqSearch('')}
                  aria-label="Clear search"
                >
                  <CloseIcon width={14} height={14} />
                </button>
              )}
            </div>

            <div className="faq-categories-row">
              {[
                { id: 'all', label: 'All Questions' },
                { id: 'admissions', label: 'Admissions & Batches' },
                { id: 'live_floor', label: 'Live Trading Floor' },
                { id: 'curriculum', label: 'Curriculum & Rules' },
                { id: 'support', label: 'Mentorship & Support' },
              ].map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`faq-cat-pill ${faqCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setFaqCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="faq-accordion-wrap">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, idx) => (
                <div key={idx} className={`faq-item ${openFaq === idx ? 'expanded' : ''}`}>
                  <button
                    type="button"
                    className="faq-question-btn"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    aria-expanded={openFaq === idx}
                  >
                    <span>{faq.q}</span>
                    <span className="faq-toggle-icon">{openFaq === idx ? '−' : '+'}</span>
                  </button>
                  {openFaq === idx && (
                    <div className="faq-answer-panel">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="faq-empty-state">
                <p>No questions matched your search query &ldquo;{faqSearch}&rdquo;.</p>
                <button
                  type="button"
                  className="btn-reset-faq"
                  onClick={() => { setFaqSearch(''); setFaqCategory('all'); }}
                >
                  Reset FAQ Filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ===================================================================
            SECTION 07: PHYSICAL PUNE CENTER & COUNSELING BOOKING (WITH REAL PHOTO)
            =================================================================== */}
        <section id="contact" className="content-section">
          <div className="contact-enrollment-card">
            {/* Left: Physical Center Contact & Direct Helpline */}
            <div className="contact-details-col">
              <span className="eyebrow-tag">07 // VISIT OUR CAMPUS</span>
              <h2 className="contact-title">Visit Our Pune Center or Schedule a Counseling Call</h2>
              <p className="contact-desc">
                Meet our NISM certified faculty in person, inspect our live market trading floor, and get your personalized learning roadmap.
              </p>

              {/* Real Campus Photo Banner */}
              <div className="campus-image-preview-box">
                <img
                  src="/assets/bsm_mentor_desk.jpg"
                  alt="Brilliant Share Market Academy Mentorship & Counseling Room"
                  className="campus-mentor-img"
                />
                <span className="campus-img-caption">Personalized 1-on-1 Trade Journal Counseling Desk</span>
              </div>

              <div className="info-items-stack">
                <div className="info-item">
                  <PinIcon className="info-ico" />
                  <div>
                    <strong>Academy Campus Address:</strong>
                    <p>
                      Besides Hotel Radha Krishna, PL NO 4, F/2 Omkar App, Sec 20, Kasturi Market Rd, Krushna Nagar, Pimpri-Chinchwad, Pune, Maharashtra 411019.
                    </p>
                  </div>
                </div>

                <div className="info-item">
                  <PhoneIcon width={18} height={18} className="info-ico" />
                  <div>
                    <strong>Admissions Helpline:</strong>
                    <p>
                      <a href="tel:+918177887927">+91 81778 87927</a> &bull; Direct WhatsApp Desk Available
                    </p>
                  </div>
                </div>

                <div className="info-item">
                  <MailIcon className="info-ico" />
                  <div>
                    <strong>Official Email:</strong>
                    <p>
                      <a href="mailto:brilliantsharemarket@gmail.com">brilliantsharemarket@gmail.com</a>
                    </p>
                  </div>
                </div>

                <div className="info-item">
                  <ClockIcon width={18} height={18} className="info-ico" />
                  <div>
                    <strong>Campus &amp; Counseling Hours:</strong>
                    <p>Monday to Saturday: 9:00 AM – 7:00 PM &bull; Sunday by Prior Appointment</p>
                  </div>
                </div>
              </div>

              {/* Interactive Transit & Commute Card */}
              <TransitGuideCard />

              {/* Direct Maps Link */}
              <div className="center-map-action">
                <a
                  href="https://maps.google.com/?q=Brilliant+Share+Market+Academy+Pimpri+Chinchwad"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-open-map"
                >
                  <ExternalLinkIcon width={14} height={14} />
                  <span>Open Directions on Google Maps</span>
                </a>
              </div>
            </div>

            {/* Right: Direct Admissions Counseling Form */}
            <div className="counseling-form-col">
              <div className="form-card-head">
                <span className="form-badge">FAST TRACK ADMISSION</span>
                <h3>Book Free Consultation &amp; Seat</h3>
                <p>Speak directly with an academic mentor. No sales agents.</p>
              </div>

              <form className="counseling-form" onSubmit={(e) => { e.preventDefault(); handleWhatsAppEnroll(); }}>
                <div className="form-row-2">
                  <div className="form-input-group">
                    <label htmlFor="counsel-name">Full Name *</label>
                    <input
                      id="counsel-name"
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={enrollForm.name}
                      onChange={(e) => setEnrollForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="form-input-group">
                    <label htmlFor="counsel-phone">Phone Number (WhatsApp) *</label>
                    <input
                      id="counsel-phone"
                      type="tel"
                      required
                      placeholder="e.g. 98765 43210"
                      value={enrollForm.phone}
                      onChange={(e) => setEnrollForm((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-input-group">
                  <label htmlFor="counsel-email">Email Address</label>
                  <input
                    id="counsel-email"
                    type="email"
                    placeholder="e.g. rahul@example.com"
                    value={enrollForm.email}
                    onChange={(e) => setEnrollForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>

                <div className="form-input-group">
                  <label htmlFor="counsel-course">Select Preferred Program</label>
                  <select
                    id="counsel-course"
                    value={enrollForm.course}
                    onChange={(e) => setEnrollForm((prev) => ({ ...prev, course: e.target.value }))}
                  >
                    <option value="Beginner's Stock Market Course">Beginner's Stock Market Course (Level 1)</option>
                    <option value="Swing Master (Power of Equity)">Swing Master - Power of Equity (Level 2)</option>
                    <option value="Master Course (Full-Time Trader)">Master Course F&amp;O Intraday (Level 3)</option>
                    <option value="Free Counseling & Demo">General Counseling / Not Sure</option>
                  </select>
                </div>

                <div className="form-row-2">
                  <div className="form-input-group">
                    <label htmlFor="counsel-mode">Training Mode</label>
                    <select
                      id="counsel-mode"
                      value={enrollForm.preferredMode}
                      onChange={(e) => setEnrollForm((prev) => ({ ...prev, preferredMode: e.target.value }))}
                    >
                      <option value="Classroom at Pune Center (Akurdi)">Pune Classroom (Akurdi)</option>
                      <option value="Live Interactive Online (Zoom)">Live Online (Zoom)</option>
                    </select>
                  </div>
                  <div className="form-input-group">
                    <label htmlFor="counsel-exp">Current Experience</label>
                    <select
                      id="counsel-exp"
                      value={enrollForm.experience}
                      onChange={(e) => setEnrollForm((prev) => ({ ...prev, experience: e.target.value }))}
                    >
                      <option value="Complete Beginner">Complete Beginner (0 yrs)</option>
                      <option value="Basic Knowledge">Basic Knowledge (&lt; 1 yr)</option>
                      <option value="Active Trader">Active Trader Seeking Edge</option>
                    </select>
                  </div>
                </div>

                <div className="form-input-group">
                  <label htmlFor="counsel-message">Questions or Specific Objectives</label>
                  <textarea
                    id="counsel-message"
                    rows="3"
                    placeholder="Tell us what you want to achieve or ask about batch timings..."
                    value={enrollForm.message}
                    onChange={(e) => setEnrollForm((prev) => ({ ...prev, message: e.target.value }))}
                  />
                </div>

                <div className="form-actions-cluster">
                  <button
                    type="submit"
                    className="btn-submit-whatsapp"
                  >
                    <WhatsAppIcon width={16} height={16} />
                    <span>Submit &amp; Connect via WhatsApp Desk</span>
                  </button>

                  <div className="form-or-divider">
                    <span>OR SUBMIT VIA EMAIL</span>
                  </div>

                  <button
                    type="button"
                    className="btn-submit-gmail"
                    onClick={() => handleEmailEnroll('gmail')}
                  >
                    <MailIcon width={15} height={15} />
                    <span>Send Query via Gmail</span>
                  </button>
                </div>

                {formSentMethod && (
                  <div className="form-feedback-alert">
                    <CheckIcon width={14} height={14} />
                    <span>Enquiry initialized! An academic counselor will review seat availability and contact you shortly.</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* 5. Production Institutional Footer */}
      <footer className="bsm-footer">
        <div className="bsm-footer-top">
          <div className="footer-brand-col">
            <div className="bsm-brand-box">
              <div className="bsm-logo-shield">
                <ActivityIcon width={20} height={20} />
              </div>
              <div className="bsm-brand-titles">
                <span className="brand-main">BRILLIANT SHARE MARKET ACADEMY</span>
                <span className="brand-trust-meta">PUNE CENTER &bull; ESTD. 2016</span>
              </div>
            </div>
            <p className="footer-desc">
              Pimpri-Chinchwad&apos;s authoritative trading training institution. Dedicated to creating disciplined, independent market participants through rigorous live market floor practice and mathematical risk management.
            </p>
            <div className="footer-contact-chips">
              <span className="footer-chip-item">
                <PinIcon width={14} height={14} className="footer-chip-icon" />
                <span>Beside Hotel Radha Krishna, PL NO 4, F/2 Omkar App, Sec 20, Kasturi Market Rd, Pune 411019</span>
              </span>
              <span className="footer-chip-item">
                <PhoneIcon width={13} height={13} className="footer-chip-icon" />
                <span>+91 81778 87927</span>
                <span className="footer-chip-sep">&bull;</span>
                <MailIcon width={13} height={13} className="footer-chip-icon" />
                <span>brilliantsharemarket@gmail.com</span>
              </span>
            </div>
          </div>

          <div className="footer-col">
            <h4>ACADEMIC PATHWAYS</h4>
            <ul className="footer-links">
              <li><a href="#courses" onClick={() => handleOpenEnroll("Beginner's Stock Market Course")}>Beginner&apos;s Foundation</a></li>
              <li><a href="#courses" onClick={() => handleOpenEnroll("Swing Master (Power of Equity)")}>Swing Master (Power of Equity)</a></li>
              <li><a href="#courses" onClick={() => handleOpenEnroll("Master Course (Full-Time Trader)")}>Master Course F&amp;O Intraday</a></li>
              <li><a href="#schedule">Upcoming Batch Calendar</a></li>
              <li><a href="#terminal">Institutional Trading Desk</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>STUDENT RESOURCES</h4>
            <ul className="footer-links">
              <li><button type="button" className="footer-link-btn" onClick={() => handleOpenEnroll("General Counseling & Demo")}>Download Brochure &amp; Fee Sheet</button></li>
              <li><a href="#terminal" onClick={() => setDashboardTab('calc')}>Position Sizing Calculator</a></li>
              <li><a href="#terminal" onClick={() => setDashboardTab('board')}>Live NSE/BSE Market Watch</a></li>
              <li><a href="#contact">Akurdi Center Directions</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>REGULATORY NOTICE</h4>
            <div className="compliance-box">
              <p>
                <strong>Educational Disclaimer:</strong> Brilliant Share Market Academy is an educational training institution.
              </p>
              <p>
                We do NOT provide stock recommendations, advisory calls, or guaranteed return schemes. Stock and derivatives trading involve substantial risk of loss. Always trade with risk capital under strict position sizing.
              </p>
            </div>
          </div>
        </div>

        <div className="bsm-footer-bottom">
          <p className="copyright-text">
            &copy; {new Date().getFullYear()} Brilliant Share Market Academy. All rights reserved. Registered in Pune, Maharashtra.
          </p>
          <div className="footer-bottom-links">
            <Link to="/" className="studio-back" title="Return to Bramhaware Technologies Home">
              ← Return to Bramhaware Technologies
            </Link>
          </div>
        </div>
      </footer>

      {/* 6. High-Conversion Quick Enrollment Modal */}
      <EnrollmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        enrollForm={enrollForm}
        setEnrollForm={setEnrollForm}
        handleWhatsAppEnroll={handleWhatsAppEnroll}
        handleEmailEnroll={handleEmailEnroll}
        formSentMethod={formSentMethod}
      />

      {/* 7. Download Syllabus Modal */}
      <SyllabusModal
        courseTitle={syllabusModalCourse}
        onClose={() => setSyllabusModalCourse(null)}
        onEnroll={(title) => {
          handleOpenEnroll(title);
          setSyllabusModalCourse(null);
        }}
      />

      {/* 8. Sticky Bottom Conversion Pill */}
      <StickyConversionDock onOpenCounseling={handleOpenEnroll} />
    </div>
  );
}
